/**
 * Risk Filter Engine - Filter Evaluation Logic (Phase 1: Row-Level Filtering)
 * 
 * Pure functions for evaluating filter specifications against risk data.
 * All functions are side-effect free and suitable for use in useMemo.
 */

import Decimal from "decimal.js";
import type { TaggedRiskRow } from "@/lib/api/riskV3";
import type {
  FilterNode,
  FilterCondition,
  FilterAnd,
  FilterOr,
  FilterNot,
  RowFilterField,
  FilterOperator,
  FilterValue,
  RiskFilterSpec,
  FilteredRiskData,
} from "./types";

// ============================================================================
// Field Value Resolution
// ============================================================================

/**
 * Resolve a field path to its value on a TaggedRiskRow.
 * Handles dot-notation paths like "meta.is_hta" and "release_data.trader_id"
 */
function resolveFieldValue(row: TaggedRiskRow, field: RowFilterField): unknown {
  // Handle top-level fields
  if (field === "customer_id") return row.customer_id;
  if (field === "commodity_group") return row.meta.commodity_group;
  if (field === "row_type") return row.row_type;
  
  // Handle meta.* fields
  if (field.startsWith("meta.")) {
    const metaField = field.substring(5); // Remove "meta." prefix
    return (row.meta as unknown as Record<string, unknown>)[metaField];
  }
  
  // Handle release_data.* fields
  if (field.startsWith("release_data.")) {
    if (!row.release_data) return null;
    const releaseField = field.substring(13); // Remove "release_data." prefix
    return (row.release_data as unknown as Record<string, unknown>)[releaseField];
  }
  
  // Handle position_data.* fields (for future use)
  if (field.startsWith("position_data.")) {
    if (!row.position_data) return null;
    const positionField = field.substring(14); // Remove "position_data." prefix
    return (row.position_data as unknown as Record<string, unknown>)[positionField];
  }
  
  // Unknown field
  return undefined;
}

// ============================================================================
// Operator Evaluation
// ============================================================================

/**
 * Parse a value to Decimal for numeric comparisons.
 * Handles string decimals, numbers, and null.
 */
function toDecimal(value: unknown): Decimal | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return new Decimal(value);
  if (typeof value === "string") {
    try {
      return new Decimal(value);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Evaluate a single condition operator against a field value.
 */
function evaluateOperator(
  fieldValue: unknown,
  operator: FilterOperator,
  filterValue: FilterValue
): boolean {
  switch (operator) {
    case "eq":
      return fieldValue === filterValue;
    
    case "neq":
      return fieldValue !== filterValue;
    
    case "in":
      if (!Array.isArray(filterValue)) return false;
      return (filterValue as (string | number)[]).includes(fieldValue as string | number);
    
    case "not_in":
      if (!Array.isArray(filterValue)) return false;
      return !(filterValue as (string | number)[]).includes(fieldValue as string | number);
    
    case "is_null":
      return fieldValue === null || fieldValue === undefined;
    
    case "is_not_null":
      return fieldValue !== null && fieldValue !== undefined;
    
    case "is_true":
      return fieldValue === true;
    
    case "is_false":
      return fieldValue === false;
    
    case "gt": {
      const fieldDecimal = toDecimal(fieldValue);
      const filterDecimal = toDecimal(filterValue);
      if (!fieldDecimal || !filterDecimal) return false;
      return fieldDecimal.gt(filterDecimal);
    }
    
    case "gte": {
      const fieldDecimal = toDecimal(fieldValue);
      const filterDecimal = toDecimal(filterValue);
      if (!fieldDecimal || !filterDecimal) return false;
      return fieldDecimal.gte(filterDecimal);
    }
    
    case "lt": {
      const fieldDecimal = toDecimal(fieldValue);
      const filterDecimal = toDecimal(filterValue);
      if (!fieldDecimal || !filterDecimal) return false;
      return fieldDecimal.lt(filterDecimal);
    }
    
    case "lte": {
      const fieldDecimal = toDecimal(fieldValue);
      const filterDecimal = toDecimal(filterValue);
      if (!fieldDecimal || !filterDecimal) return false;
      return fieldDecimal.lte(filterDecimal);
    }
    
    default:
      // Unknown operator - fail safe
      return false;
  }
}

// ============================================================================
// Filter Tree Evaluation
// ============================================================================

/**
 * Evaluate a filter condition against a row.
 */
function evaluateCondition(row: TaggedRiskRow, condition: FilterCondition): boolean {
  const fieldValue = resolveFieldValue(row, condition.field);
  return evaluateOperator(fieldValue, condition.operator, condition.value);
}

/**
 * Evaluate an AND node - all children must match.
 */
function evaluateAnd(row: TaggedRiskRow, andNode: FilterAnd): boolean {
  if (andNode.children.length === 0) return true; // Empty AND is true
  return andNode.children.every((child) => evaluateFilterNode(row, child));
}

/**
 * Evaluate an OR node - at least one child must match.
 */
function evaluateOr(row: TaggedRiskRow, orNode: FilterOr): boolean {
  if (orNode.children.length === 0) return false; // Empty OR is false
  return orNode.children.some((child) => evaluateFilterNode(row, child));
}

/**
 * Evaluate a NOT node - negates its child.
 */
function evaluateNot(row: TaggedRiskRow, notNode: FilterNot): boolean {
  return !evaluateFilterNode(row, notNode.child);
}

/**
 * Evaluate a filter node against a row.
 * This is the main recursive entry point for the filter tree.
 */
function evaluateFilterNode(row: TaggedRiskRow, node: FilterNode): boolean {
  switch (node.type) {
    case "and":
      return evaluateAnd(row, node);
    case "or":
      return evaluateOr(row, node);
    case "not":
      return evaluateNot(row, node);
    case "condition":
      return evaluateCondition(row, node);
    default:
      // Unknown node type - fail safe
      return false;
  }
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Apply a row filter to an array of rows.
 * Returns only the rows that match the filter.
 * 
 * @param rows - Array of TaggedRiskRow to filter
 * @param filter - Filter tree to apply (undefined means no filtering)
 * @returns Filtered array of rows
 */
export function applyRowFilter(
  rows: TaggedRiskRow[],
  filter: FilterNode | undefined
): TaggedRiskRow[] {
  // No filter = return all rows
  if (!filter) return rows;
  
  // Filter rows using the filter tree
  return rows.filter((row) => evaluateFilterNode(row, filter));
}

/**
 * Apply a complete RiskFilterSpec to raw risk data.
 * This is the main entry point for the filter engine.
 * 
 * @param rows - Raw TaggedRiskRow array from the V3 API
 * @param spec - Filter specification
 * @returns Filtered risk data
 */
export function applyFilterSpec(
  rows: TaggedRiskRow[],
  spec: RiskFilterSpec
): FilteredRiskData {
  // Phase 1: Apply row-level filter
  const filteredRows = applyRowFilter(rows, spec.row_filter);
  
  return {
    rows: filteredRows,
    total_count: filteredRows?.length ?? 0,
  };
}

// ============================================================================
// Filter Composition Utilities
// ============================================================================

/**
 * Compose multiple filter nodes into a single AND node.
 * Filters out undefined nodes.
 * 
 * @param nodes - Filter nodes to compose
 * @returns Single composed filter node, or undefined if no nodes provided
 */
export function composeFiltersAnd(
  ...nodes: (FilterNode | undefined)[]
): FilterNode | undefined {
  const defined = nodes.filter((n): n is FilterNode => n !== undefined);
  
  if (defined.length === 0) return undefined;
  if (defined.length === 1) return defined[0];
  
  return {
    type: "and",
    children: defined,
  };
}

/**
 * Compose multiple filter nodes into a single OR node.
 * Filters out undefined nodes.
 * 
 * @param nodes - Filter nodes to compose
 * @returns Single composed filter node, or undefined if no nodes provided
 */
export function composeFiltersOr(
  ...nodes: (FilterNode | undefined)[]
): FilterNode | undefined {
  const defined = nodes.filter((n): n is FilterNode => n !== undefined);
  
  if (defined.length === 0) return undefined;
  if (defined.length === 1) return defined[0];
  
  return {
    type: "or",
    children: defined,
  };
}
